import React, { useState } from "react";
import { toast } from "react-toastify";
import Dialog from "../../../components/ui/Dialog";
import {
  useCreateSyncConfig,
  useSyncOverview,
  useUpdateSyncConfig,
} from "../../../api/queries/adminQueries";
import { useCompanies } from "../../../api/queries/organizationQueries";
import { getApiErrorMessage } from "../../../utils/apiError";
import { formatDateTime } from "../../../utils/dateTime";
import {
  buildSyncPayload,
  cronFromTime,
  cronSentence,
  emptySyncForm,
  syncFormFromConfig,
  timeFromCron,
} from "./syncFormat";

const EMPTY = [];
const SAVED_HINT = "Saved. Leave blank to keep it.";

const FolderSet = ({ id }) =>
  id ? (
    <span className="status-pill status-pill--green" title={id}>
      Set
    </span>
  ) : (
    <span className="status-pill status-pill--gray">Not set</span>
  );

const LastRun = ({ run }) => {
  if (!run) return <span className="text-slate-400">Never</span>;
  const failed = run.status === "FAILED" || run.failureCount > 0;
  return (
    <>
      <div className="text-nowrap">{formatDateTime(run.createdAt)}</div>
      <div className={failed ? "text-danger" : "text-slate-500"}>
        {run.successCount ?? 0} linked · {run.failureCount ?? 0} failed
        {run.partialCount > 0 ? ` · ${run.partialCount} not moved` : ""}
      </div>
    </>
  );
};

/** The branches of the task's company and the folders it would scan. */
const FoldersSection = ({ companyId }) => {
  const query = useSyncOverview(companyId);
  const branches = query.data?.branches || EMPTY;
  return (
    <div className="col-12">
      <h3 className="profile-label mb-1">Folders this task scans</h3>
      <p className="fs-075 text-slate-500 mb-2">
        Folders are set on each branch. This task only covers branches of its
        own company, and branches without a source folder are skipped.
      </p>
      {query.data?.scheduleEnabled === false && (
        <div className="alert alert-warning fs-080 py-2" role="status">
          Schedules are switched off on this server. Run Now still works.
        </div>
      )}
      {query.isLoading && (
        <div className="fs-080 text-slate-500">Loading branches…</div>
      )}
      {query.isError && (
        <div className="fs-080 text-danger">
          {getApiErrorMessage(query.error, "Could not load the branches.")}
        </div>
      )}
      {query.isSuccess && branches.length === 0 && (
        <div className="fs-080 text-slate-500">
          This company has no branches.
        </div>
      )}
      {branches.length > 0 && (
        <div className="table-responsive sync-folders">
          <table className="table table-sm mb-0 fs-080">
            <thead>
              <tr>
                <th>Branch</th>
                <th>Source folder</th>
                <th>Target folder</th>
                <th>Last run</th>
              </tr>
            </thead>
            <tbody>
              {branches.map((b) => (
                <tr key={b.id}>
                  <td className="fw-semibold">{b.name}</td>
                  <td>
                    <FolderSet id={b.sourceFolderId} />
                  </td>
                  <td>
                    <FolderSet id={b.targetFolderId} />
                  </td>
                  <td>
                    <LastRun run={b.lastRun} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

/** Create or edit a Zoho sync task. Secrets are write-only: they never come back. */
const SyncConfigDialog = ({ config, defaultCompanyId, onClose }) => {
  const isEdit = !!config;
  const companies = useCompanies().data || EMPTY;
  const create = useCreateSyncConfig();
  const update = useUpdateSyncConfig();
  const saving = create.isPending || update.isPending;
  const credentials = config?.source?.credentials;
  const hasSecret = !!credentials?.hasClientSecret;
  const hasToken = !!credentials?.hasRefreshToken;

  const [form, setForm] = useState(() =>
    isEdit ? syncFormFromConfig(config) : emptySyncForm(defaultCompanyId || ""),
  );
  const initial = timeFromCron(form.cronExpression);
  const [time, setTime] = useState(initial?.time || "02:00");
  const [frequency, setFrequency] = useState(initial?.frequency || "daily");

  const set = (changes) => setForm((prev) => ({ ...prev, ...changes }));
  // A custom schedule stays as it is until someone changes the time here
  const changeSchedule = (nextTime, nextFrequency) => {
    setTime(nextTime);
    setFrequency(nextFrequency);
    set({ cronExpression: cronFromTime(nextTime, nextFrequency) });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const payload = buildSyncPayload(form);
    try {
      if (isEdit) await update.mutateAsync({ id: config.id, dto: payload });
      else await create.mutateAsync(payload);
      toast.success("Configuration saved successfully!");
      onClose();
    } catch (err) {
      toast.error(getApiErrorMessage(err, "Failed to save configuration"));
    }
  };

  return (
    <Dialog
      title="Zoho Configuration"
      onClose={onClose}
      size="lg"
      busy={saving}
    >
      <form onSubmit={handleSubmit}>
        <div className="row g-3">
          <div className="col-md-6">
            <label htmlFor="sync-name" className="profile-label">
              Task name <span className="text-danger">*</span>
            </label>
            <input
              id="sync-name"
              className="form-control"
              value={form.name}
              onChange={(e) => set({ name: e.target.value })}
              required
            />
          </div>
          <div className="col-md-6">
            <label htmlFor="sync-company" className="profile-label">
              Company <span className="text-danger">*</span>
            </label>
            <select
              id="sync-company"
              className="form-select"
              value={form.companyId}
              onChange={(e) => set({ companyId: e.target.value })}
              required
            >
              <option value="">Select a company</option>
              {form.companyId &&
                !companies.some((c) => c.id === form.companyId) && (
                  <option value={form.companyId}>Current company</option>
                )}
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.companyName}
                </option>
              ))}
            </select>
          </div>

          <div className="col-md-6">
            <label htmlFor="sync-time" className="profile-label">
              Start time (Manila time)
            </label>
            <input
              id="sync-time"
              type="time"
              className="form-control"
              value={time}
              onChange={(e) => changeSchedule(e.target.value, frequency)}
              required
            />
          </div>
          <div className="col-md-6">
            <label htmlFor="sync-frequency" className="profile-label">
              Frequency
            </label>
            <select
              id="sync-frequency"
              className="form-select"
              value={frequency}
              onChange={(e) => changeSchedule(time, e.target.value)}
            >
              <option value="daily">Daily</option>
              <option value="weekly">Weekly (Sunday)</option>
            </select>
          </div>
          <div className="col-12 fs-080">
            <i className="bi bi-calendar-event me-1"></i>
            {cronSentence(form.cronExpression)}
            <code className="ms-2 fs-070 text-slate-500">
              {form.cronExpression}
            </code>
          </div>

          <div className="col-12">
            <div className="form-check form-switch">
              <input
                id="sync-active"
                className="form-check-input"
                type="checkbox"
                checked={form.isActive}
                onChange={(e) => set({ isActive: e.target.checked })}
              />
              <label
                htmlFor="sync-active"
                className="form-check-label small fw-bold"
              >
                Active schedule
              </label>
            </div>
            <div className="fs-070 text-slate-500">
              On: runs by itself at this time. Off: it only runs when someone
              presses Run Now.
            </div>
          </div>

          <div className="col-12">
            <div className="p-3 bg-white rounded-3 border">
              <div className="fw-bold small text-primary text-uppercase fs-065">
                Zoho credentials
              </div>
              <p className="fs-070 text-slate-500 mb-2">
                From the Zoho API Console. The token needs WorkDrive access.
              </p>
              <div className="row g-2">
                <div className="col-md-6">
                  <label htmlFor="sync-client-id" className="profile-label">
                    Client ID <span className="text-danger">*</span>
                  </label>
                  <input
                    id="sync-client-id"
                    className="form-control form-control-sm"
                    autoComplete="off"
                    value={form.clientId}
                    onChange={(e) => set({ clientId: e.target.value })}
                    required
                  />
                </div>
                <div className="col-md-6">
                  <label htmlFor="sync-secret" className="profile-label">
                    Client Secret{" "}
                    {(!isEdit || !hasSecret) && (
                      <span className="text-danger">*</span>
                    )}
                  </label>
                  <input
                    id="sync-secret"
                    type="password"
                    className="form-control form-control-sm"
                    autoComplete="new-password"
                    value={form.clientSecret}
                    onChange={(e) => set({ clientSecret: e.target.value })}
                    required={!isEdit || !hasSecret}
                  />
                  {isEdit && hasSecret && (
                    <div className="fs-070 text-slate-500 mt-1">
                      {SAVED_HINT}
                    </div>
                  )}
                </div>
                <div className="col-12">
                  <label htmlFor="sync-token" className="profile-label">
                    Refresh Token{" "}
                    {(!isEdit || !hasToken) && (
                      <span className="text-danger">*</span>
                    )}
                  </label>
                  <input
                    id="sync-token"
                    type="password"
                    className="form-control form-control-sm"
                    autoComplete="new-password"
                    value={form.refreshToken}
                    onChange={(e) => set({ refreshToken: e.target.value })}
                    required={!isEdit || !hasToken}
                  />
                  {isEdit && hasToken && (
                    <div className="fs-070 text-slate-500 mt-1">
                      {SAVED_HINT}
                    </div>
                  )}
                </div>
                <div className="col-md-6">
                  <label htmlFor="sync-dc" className="profile-label">
                    Data center
                  </label>
                  <select
                    id="sync-dc"
                    className="form-select form-select-sm"
                    value={form.dc}
                    onChange={(e) => set({ dc: e.target.value })}
                  >
                    <option value="com">.com (US)</option>
                    <option value="in">.in (India)</option>
                  </select>
                  <div className="fs-070 text-slate-500 mt-1">
                    File calls use Zoho's .com servers, so a .in account may not
                    fully work.
                  </div>
                </div>
                <div className="col-md-6">
                  <label htmlFor="sync-target" className="profile-label">
                    Default target folder ID (optional)
                  </label>
                  <input
                    id="sync-target"
                    className="form-control form-control-sm"
                    value={form.targetIdentifier}
                    onChange={(e) => set({ targetIdentifier: e.target.value })}
                  />
                  <div className="fs-070 text-slate-500 mt-1">
                    Where linked files go when a branch has no target folder of
                    its own.
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="col-12 fs-075 text-slate-600">
            <strong>File names:</strong> LastName-Birthdate-DocType, e.g.{" "}
            <code>DELA CRUZ-19830519-SSS.pdf</code>. Birthdate may be YYYYMMDD
            or MMDDYYYY. Files are matched to employees by last name and
            birthday.
          </div>

          {form.companyId && <FoldersSection companyId={form.companyId} />}

          <div className="col-12 pt-2">
            <button
              type="submit"
              className="btn btn-dark w-100 rounded-pill fw-bold py-2 shadow"
              disabled={saving}
            >
              {saving ? "Saving..." : "Save Configuration"}
            </button>
          </div>
        </div>
      </form>
    </Dialog>
  );
};

export default SyncConfigDialog;
